import { useCallback, useEffect, useState } from 'react';
import { listFeedback, runTick, type FeedbackItem, type QueueFilters } from '../api';
import { type FeedbackLocale, type FeedbackTranslate } from '../i18n';
import {
  Button,
  Card,
  DataTable,
  Drawer,
  Field,
  Select,
  StatusBadge,
  Moment,
  type Column,
  Checkbox,
} from '../../../shell/kit';
import { useToast } from '../../../shell/toast';
import { useCategoryCatalog } from '../catalog';
import { getFeedback } from '../api';
import { FeedbackCard } from './card';
import { errorText } from '../../../shell/errors';

const STATUSES = ['new', 'assigned', 'answered', 'escalated', 'closed'] as const;
const CHANNELS = ['qr', 'whatsapp', 'app', 'phone', 'import'] as const;

export function QueueView({
  t,
  locale,
  canAnswer,
  canTick,
  siteId,
}: {
  t: FeedbackTranslate;
  locale: FeedbackLocale;
  canAnswer: boolean;
  canTick: boolean;
  siteId: string | null;
}) {
  const toast = useToast();
  const { categoryName } = useCategoryCatalog();
  const [filters, setFilters] = useState<QueueFilters>({});
  const [items, setItems] = useState<readonly FeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<FeedbackItem | null>(null);
  const [busy, setBusy] = useState(false);

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [locale, toast],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      /*
       * Объект берётся из переключателя оболочки, а не из фильтров экрана: он
       * приходил пропсом и не использовался вовсе, поэтому очередь показывала
       * все доступные роли объекты сразу и не менялась при переключении.
       */
      const page = await listFeedback({ ...filters, ...(siteId ? { siteId } : {}) });
      setItems(page.items);
    } catch (err) {
      fail(err);
    } finally {
      setLoading(false);
    }
  }, [filters, siteId, fail]);

  /**
   * Карточка перечитывается целиком: список не отдаёт список допустимых переходов,
   * а именно по нему гейтятся кнопки «Ответить», «Закрыть», «Эскалировать».
   */
  const open = async (row: FeedbackItem): Promise<void> => {
    setSelected(row);
    try {
      setSelected(await getFeedback(row.id));
    } catch (err) {
      fail(err);
    }
  };

  useEffect(() => {
    void load();
  }, [load]);

  const tick = async (): Promise<void> => {
    setBusy(true);
    try {
      const result = await runTick();
      toast.ok(t('feedback.queue.ticked', { reminded: result.reminded, overdue: result.overdue }));
      await load();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<FeedbackItem>> = [
    {
      key: 'status',
      header: t('feedback.queue.status'),
      render: (row) => (
        <span className="flex flex-col gap-1">
          <StatusBadge
            tone={
              row.status === 'closed'
                ? 'ok'
                : row.status === 'escalated'
                  ? 'danger'
                  : row.overdue
                    ? 'warn'
                    : 'neutral'
            }
          >
            {t(`feedback.status.${row.status}` as never)}
          </StatusBadge>
          {row.overdue ? (
            <span className="text-xs font-medium text-danger">{t('feedback.queue.overdue')}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'channel',
      header: t('feedback.queue.channel'),
      render: (row) => t(`feedback.channel.${row.channel}` as never),
    },
    {
      key: 'score',
      header: t('feedback.queue.score'),
      render: (row) =>
        row.score === null ? (
          '—'
        ) : (
          <span className={row.score <= 3 ? 'font-semibold text-danger' : ''}>{row.score}</span>
        ),
    },
    {
      key: 'category',
      header: t('feedback.queue.category'),
      // Список печатал сырые ключи (wash_quality, streaks), тогда как в карточке
      // тот же справочник переведён.
      render: (row) => (
        <span className="flex flex-col">
          <span>{categoryName(row.category)}</span>
          <span className="text-xs text-ink-muted">
            {t(`feedback.category.${row.categorySource}` as never)}
          </span>
        </span>
      ),
    },
    {
      key: 'body',
      header: t('feedback.card.text'),
      render: (row) => (
        <span className="line-clamp-2 text-xs text-ink-muted">{row.body ?? '—'}</span>
      ),
    },
    {
      key: 'created',
      header: t('feedback.queue.created'),
      render: (row) => <Moment iso={row.createdAt} />,
    },
    { key: 'due', header: t('feedback.queue.due'), render: (row) => <Moment iso={row.dueAt} /> },
    {
      key: 'actions',
      header: '',
      render: (row) => <Button onClick={() => void open(row)}>{t('feedback.queue.open')}</Button>,
    },
  ];

  return (
    <div className="space-y-4">
      <Card title={t('feedback.queue.title')}>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <Field label={t('feedback.queue.status')}>
            <Select
              value={filters.status ?? ''}
              onChange={(e) => setFilters({ ...filters, status: e.target.value || undefined })}
            >
              <option value="">{t('feedback.queue.all')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`feedback.status.${s}` as never)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('feedback.queue.channel')}>
            <Select
              value={filters.channel ?? ''}
              onChange={(e) => setFilters({ ...filters, channel: e.target.value || undefined })}
            >
              <option value="">{t('feedback.queue.all')}</option>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {t(`feedback.channel.${c}` as never)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('feedback.queue.score')}>
            <Select
              value={filters.scoreMax ?? ''}
              onChange={(e) => setFilters({ ...filters, scoreMax: e.target.value || undefined })}
            >
              <option value="">{t('feedback.queue.all')}</option>
              {[1, 2, 3, 4].map((s) => (
                <option key={s} value={String(s)}>
                  ≤ {s}
                </option>
              ))}
            </Select>
          </Field>

          <label className="flex items-center gap-2 text-sm text-ink">
            <Checkbox
              checked={filters.overdue ?? false}
              onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
            />
            {t('feedback.queue.overdueOnly')}
          </label>

          <label className="flex items-center gap-2 text-sm text-ink">
            <Checkbox
              checked={filters.unlinked ?? false}
              onChange={(e) => setFilters({ ...filters, unlinked: e.target.checked })}
            />
            {t('feedback.queue.unlinkedOnly')}
          </label>

          <Button onClick={() => void load()}>{t('feedback.queue.refresh')}</Button>
          {canTick ? (
            <Button disabled={busy} onClick={() => void tick()}>
              {t('feedback.queue.tick')}
            </Button>
          ) : null}
        </div>

        <DataTable
          loading={loading}
          rows={items}
          columns={columns}
          empty={t('feedback.queue.empty')}
          rowKey={(row) => row.id}
        />
      </Card>

      {/* Карточка выезжает поверх экрана: раньше она рисовалась под таблицей,
          и на длинной очереди попадала за пределы вьюпорта. */}
      {selected ? (
        <Drawer title={t('feedback.card.title')} onClose={() => setSelected(null)}>
          <FeedbackCard
            siteId={siteId}
            item={selected}
            t={t}
            locale={locale}
            canAnswer={canAnswer}
            onChanged={(next) => {
              setSelected(next);
              setItems((prev) => prev.map((row) => (row.id === next.id ? next : row)));
            }}
            onClose={() => setSelected(null)}
          />
        </Drawer>
      ) : null}
    </div>
  );
}
